import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/solid';
import { Button } from '@heroui/react';
import { type FC, useEffect, useState } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import toast from '@/app/toast.ts';
import { Badge } from '@/primitives/badge.tsx';
import { Divider } from '@/primitives/divider.tsx';
import { Subheading } from '@/primitives/heading.tsx';
import {
  useDeleteAccountSiteStaffMemberMutation,
  usePostAccountSiteStaffMemberMutation,
  usePutAccountSiteStaffMemberMutation,
} from '@/slices/account-site-staff-member-api-slice.ts';
import type { AccountSiteStaffMemberModel } from '../../types/account-site-staff-member.type.ts';
import GenericAlert from '../generic-alert.tsx';
import { usePageLoading } from '../page-loading.tsx';
import AccountSiteStaffMemberFormFields from './account-site-staff-member-form-fields.tsx';

interface AccountSiteStaffMemberFormProps {
  data: AccountSiteStaffMemberModel;
  onSave?: () => void;
  onRemove?: () => void;
  hideRemove?: boolean;
}

const AccountSiteStaffMemberForm: FC<AccountSiteStaffMemberFormProps> = ({
  data,
  onSave,
  onRemove,
  hideRemove,
}) => {
  const form = useForm<{ accountSiteStaffMember: AccountSiteStaffMemberModel }>({
    values: {
      accountSiteStaffMember: data,
    },
  });
  const { handleSubmit } = form;
  const [save, { isLoading, isError, isSuccess }] = data?.id
    ? // biome-ignore lint/correctness/useHookAtTopLevel: fix me
      usePutAccountSiteStaffMemberMutation()
    : // biome-ignore lint/correctness/useHookAtTopLevel: fix me
      usePostAccountSiteStaffMemberMutation();
  const [deleteAccount] = useDeleteAccountSiteStaffMemberMutation();
  const [alertOpen, setAlertOpen] = useState(false);

  const remove = () => {
    deleteAccount(data.key)
      .unwrap()
      .then(() => !!onRemove && onRemove());
  };

  const onSubmit: SubmitHandler<{
    accountSiteStaffMember: AccountSiteStaffMemberModel;
  }> = (value: { accountSiteStaffMember: AccountSiteStaffMemberModel }) => {
    save(value.accountSiteStaffMember)
      .unwrap()
      .then(() => !!onSave && onSave());
  };

  const { showPageLoading, hidePageLoading } = usePageLoading();

  useEffect(() => {
    isLoading
      ? showPageLoading(`loading-save-account-site-staff-${data?.account.key}`)
      : hidePageLoading(`loading-save-account-site-staff-${data?.account.key}`);
  }, [isLoading]);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Account access saved.');
    }

    if (isError) {
      toast.error('There was an error attempting to save the account access.');
    }
  }, [isError, isSuccess]);

  return (
    <>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className='h-full'>
          <div className='bg-white rounded-xl border-2 border-gray-300 overflow-hidden h-full flex flex-col'>
            <div className='p-6'>
              <div className='flex items-center gap-3 mb-1'>
                <Subheading>{data?.account?.accountName}</Subheading>
              </div>
              {!data?.id && (
                <Badge color='amber' className='mt-2'>
                  New - Not Saved
                </Badge>
              )}
            </div>

            <Divider />

            <div className='p-6 flex-1'>
              <AccountSiteStaffMemberFormFields data={data} name='accountSiteStaffMember' />
            </div>
            <Divider />
            <div className='p-3 bg-gray-200 flex gap-3'>
              <Button type='submit' color='primary'>
                <CheckCircleIcon className='size-5' />
                Save
              </Button>
              {!hideRemove && (
                <Button
                  color='danger'
                  variant='flat'
                  type='button'
                  onPress={() => (data?.id ? setAlertOpen(true) : onRemove())}
                >
                  <TrashIcon className='size-5' />
                  Remove
                </Button>
              )}
            </div>
          </div>
        </form>
      </FormProvider>
      <GenericAlert
        onConfirm={() => remove()}
        isOpen={alertOpen}
        message={`Are you sure you want to remove access to ${data?.account.accountName}?`}
        setIsOpen={setAlertOpen}
        title='Remove account access'
      />
    </>
  );
};

export default AccountSiteStaffMemberForm;
